import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { config } from "@/config";
import type { MeResponse } from "@/types/api";
import {
  getIdToken,
  onAuthChanged,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  consumeGoogleRedirect,
  signOut,
  type User,
} from "@/lib/firebase";

/**
 * AuthProvider — owns the Firebase user, the backend account profile (/api/me),
 * and the derived approval gate.
 *
 * Account states (per the API contract):
 *   · no user                → must sign in
 *   · user + status pending  → awaiting-approval gate
 *   · user + status active   → full access
 */

export type AuthStatus =
  | "loading"
  | "signed-out"
  | "pending"
  | "active"
  | "suspended";

interface AuthContextValue {
  user: User | null;
  me: MeResponse | null;
  status: AuthStatus;
  error: string | null;
  signInEmail: (email: string, password: string) => Promise<void>;
  signUpEmail: (email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Fetch the account profile. NOTE: production REST is CORS-locked to the app
 * origin (app.earlywire.io). If /api/me is unreachable in your environment,
 * the gate falls back to treating an authenticated user as `active` so the ops
 * screen still loads — tighten this once the origin is allowlisted.
 */
async function fetchMe(token: string): Promise<MeResponse | null> {
  try {
    // Referral attribution + single-use invites ride along on the profile fetch. The referral
    // code persists until the server confirms the stamp (me.referredBy); the invite token is
    // one-shot by definition — sent once, then dropped whatever the outcome (the server claims
    // it atomically; retrying a used token can never do anything).
    const params = new URLSearchParams();
    const ref = localStorage.getItem("ew_ref");
    const invite = localStorage.getItem("ew_invite");
    if (ref) params.set("ref", ref);
    if (invite) params.set("invite", invite);
    const qs = params.toString();
    const r = await fetch(`${config.restUrl}/api/me${qs ? `?${qs}` : ""}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    const me = (await r.json()) as MeResponse;
    if (me.referredBy) localStorage.removeItem("ew_ref");
    if (invite) localStorage.removeItem("ew_invite");
    return me;
  } catch {
    return null;
  }
}

// Capture ?ref=CODE / ?invite=TOKEN the moment ANY page loads (landing or app) — signup may
// happen much later, so both persist in localStorage until the first authenticated fetch.
try {
  const boot = new URLSearchParams(window.location.search);
  const refParam = boot.get("ref");
  if (refParam) localStorage.setItem("ew_ref", refParam.slice(0, 64));
  const inviteParam = boot.get("invite");
  if (inviteParam) localStorage.setItem("ew_invite", inviteParam.slice(0, 80));
} catch { /* SSR/test environments without window */ }

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const loadMe = async (u: User | null) => {
    if (!u) {
      setMe(null);
      setStatus("signed-out");
      return;
    }
    // A user EXISTS but the profile is still in flight: status must read "loading", not the
    // stale "signed-out" — otherwise a fresh signup's nav to /ops races the fetch, RequireAuth
    // bounces them back to /signin, and the pending gate never gets a chance to route them
    // (the "CREATE ACCOUNT hangs" bug).
    setStatus("loading");
    const token = await u.getIdToken();
    const profile = await fetchMe(token);
    setMe(profile);
    if (!profile) {
      // REST unreachable (CORS) — allow through; WS layer is the source of truth.
      setStatus("active");
    } else if (profile.entitled === false) {
      // Server-side gate says no: expired 1h trial or a suspension.
      setStatus(profile.reason === "suspended" ? "suspended" : "pending");
    } else {
      setStatus("active"); // active plan OR live trial window
    }
  };

  useEffect(() => {
    return onAuthChanged((u) => {
      setUser(u);
      void loadMe(u);
    });
  }, []);

  // Google popup-blocked → redirect flow: success resumes via onAuthChanged, but a FAILED
  // redirect return (provider disabled, domain, cancelled consent) would vanish silently
  // without this — surface it in the same error slot the sign-in form shows.
  useEffect(() => {
    consumeGoogleRedirect().catch((e) => setError(humanizeAuthError(e)));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      me,
      status,
      error,
      signInEmail: async (email, password) => {
        setError(null);
        try {
          await signInWithEmail(email, password);
        } catch (e) {
          setError(humanizeAuthError(e));
          throw e;
        }
      },
      signUpEmail: async (email, password) => {
        setError(null);
        try {
          await signUpWithEmail(email, password);
        } catch (e) {
          setError(humanizeAuthError(e));
          throw e;
        }
      },
      signInGoogle: async () => {
        setError(null);
        try {
          await signInWithGoogle();
        } catch (e) {
          setError(humanizeAuthError(e));
          throw e;
        }
      },
      signOut: async () => {
        await signOut();
      },
      refreshMe: async () => {
        await loadMe(user);
      },
    }),
    [user, me, status, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Re-export so the WS layer can be wired with the same token source. */
export { getIdToken };

function humanizeAuthError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? "";
  // Surface the raw Firebase code in the console — config errors (unauthorized
  // domain / provider disabled) are otherwise invisible since the popup never opens.
  if (code) console.warn(`[auth] ${code}`);
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Try again shortly.";
    case "auth/email-already-in-use":
      return "An account with this email already exists — sign in instead.";
    case "auth/weak-password":
      return "Passphrase must be at least 6 characters.";
    case "auth/invalid-email":
      return "That doesn't look like a valid email.";
    case "auth/popup-closed-by-user":
      return "Sign-in cancelled.";
    case "auth/popup-blocked":
      return "Pop-up blocked — allow pop-ups for this site, then try again.";
    case "auth/unauthorized-domain":
      return "This domain isn't authorized for sign-in yet (Firebase → Authentication → Settings → Authorized domains).";
    case "auth/operation-not-allowed":
      return "Google sign-in isn't enabled for this project yet (Firebase → Authentication → Sign-in method).";
    default:
      return "Sign-in failed. Please try again.";
  }
}
